/**
 * Demolition Tool
 * 
 * Marks elements for demolition in a specific phase.
 */

import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';

// ==================== Types ====================

export interface DemolitionMark {
    elementId: string;
    demolitionPhaseId: string;
    reason?: string;
    markedAt: number;
    markedBy: string;
}

// ==================== Demolition Tool ====================

export class DemolitionTool extends BaseTool {
    id = 'demolition';
    name = 'Demolition';
    cursor = 'crosshair';

    private demolitionPhaseId: string = '';
    private marks: Map<string, DemolitionMark> = new Map();

    public setDemolitionPhase(phaseId: string): void {
        this.demolitionPhaseId = phaseId;
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = event.intersections.find(i => {
            const id = this.findId(i.object);
            return id && id !== 'ground-plane';
        });

        if (hit) {
            const elementId = this.findId(hit.object)!;
            this.toggleDemolition(elementId);
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const hit = event.intersections.find(i => {
            const id = this.findId(i.object);
            return id && id !== 'ground-plane';
        });

        if (hit) {
            const elementId = this.findId(hit.object)!;
            const isMarked = this.marks.has(elementId);

            this.context.appStore.setToolPreview({
                type: 'demolition_highlight',
                elementId,
                isMarked,
                color: isMarked ? '#ff0000' : '#ffff00'
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    private toggleDemolition(elementId: string): void {
        if (this.marks.has(elementId)) {
            // Unmark
            this.marks.delete(elementId);
            console.log(`Removed demolition mark from ${elementId}`);
        } else {
            // Mark for demolition
            const mark: DemolitionMark = {
                elementId,
                demolitionPhaseId: this.demolitionPhaseId,
                markedAt: Date.now(),
                markedBy: 'Current User'
            };
            this.marks.set(elementId, mark);
            console.log(`Marked ${elementId} for demolition in phase ${this.demolitionPhaseId}`);
        }
    }

    // ==================== Batch Operations ====================

    public markMultiple(elementIds: string[], phaseId?: string): void {
        const phase = phaseId || this.demolitionPhaseId;
        for (const id of elementIds) {
            if (!this.marks.has(id)) {
                this.marks.set(id, {
                    elementId: id,
                    demolitionPhaseId: phase,
                    markedAt: Date.now(),
                    markedBy: 'Current User'
                });
            }
        }
    }

    public unmarkMultiple(elementIds: string[]): void {
        for (const id of elementIds) {
            this.marks.delete(id);
        }
    }

    public markByCategory(category: string, phaseId?: string): number {
        if (!this.context) return 0;

        let count = 0;
        const phase = phaseId || this.demolitionPhaseId;

        for (const element of this.context.bimStore.project.elements.values()) {
            if (element.category === category && !this.marks.has(element.id)) {
                this.marks.set(element.id, {
                    elementId: element.id,
                    demolitionPhaseId: phase,
                    markedAt: Date.now(),
                    markedBy: 'Current User'
                });
                count++;
            }
        }

        return count;
    }

    // ==================== Getters ====================

    public getDemolitionMark(elementId: string): DemolitionMark | undefined {
        return this.marks.get(elementId);
    }

    public getAllMarks(): DemolitionMark[] {
        return Array.from(this.marks.values());
    }

    public getMarksByPhase(phaseId: string): DemolitionMark[] {
        return Array.from(this.marks.values()).filter(m => m.demolitionPhaseId === phaseId);
    }

    public isMarkedForDemolition(elementId: string): boolean {
        return this.marks.has(elementId);
    }

    // ==================== Export ====================

    public exportDemolitionSchedule(): string {
        const lines: string[] = [
            'DEMOLITION SCHEDULE',
            '==================',
            '',
            'Element ID,Phase,Marked At,Marked By,Reason'
        ];

        for (const mark of this.marks.values()) {
            lines.push([
                mark.elementId,
                mark.demolitionPhaseId,
                new Date(mark.markedAt).toLocaleDateString(),
                mark.markedBy,
                mark.reason || ''
            ].join(','));
        }

        lines.push('');
        lines.push(`Total: ${this.marks.size} elements`);

        return lines.join('\n');
    }
}
